import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import CreatableSelect from "react-select/creatable";
import ReactFlagsSelect from "react-flags-select";
import Select from "react-select";
import {
  fetchJobById,
  updateJob,
  fetchAllSkills,
  createSkill,
  generateJobDescriptionWithAI,
  enhanceJobDescriptionWithAI,
} from "../../Redux/ActionCreators/jobsAction";
import { getClientCompanies, createClientCompany } from "../../Redux/ActionCreators/settingAction";
import countriesData from "../../utils/countries.json";
import citiesData from "../../utils/cities.json";
import { Snackbar, Alert } from "@mui/material";
import "./jobs.scss";

const countriesList =
  countriesData[
    "select id, name, iso3, numeric_code, iso2, emoji  from countries where flag =1"
  ];
const citiesList =
  citiesData["select id, name, country_id from cities where flag = 1"];

const EditJob = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const jobId = location.state?.jobId;
  const fromCreateJob = location.state?.autoGenerate === true;

  const [submitType, setSubmitType] = useState("draft");
  const [aiLoading, setAiLoading] = useState(false);
  const [notification, setNotification] = useState("");
  const [initialLoading, setInitialLoading] = useState(true);
  const [localJob, setLocalJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [isHover, setIsHover] = useState(false);
  const [triggerAI, setTriggerAI] = useState(false);
  const [artificialLoading, setArtificialLoading] = useState(fromCreateJob);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const job = useSelector((state) => state.jobs.latestJob);
  const allSkills = useSelector((state) => state.jobs.skills);
  const clientCompanies = useSelector((state) => state.setting.clientCompanies || []);
  const [skillOptions, setSkillOptions] = useState([]);

  const clientCompanyOptions = useMemo(
    () =>
      (clientCompanies || [])
        .filter((c) => c.is_active !== false)
        .map((c) => ({ value: c.id, label: c.name || c.code || c.id })),
    [clientCompanies],
  );

  const [creatingCompany, setCreatingCompany] = useState(false);

  const handleCreateCompany = async (inputValue) => {
    const name = String(inputValue || "").trim();
    if (!name) return;
    setCreatingCompany(true);
    const result = await dispatch(createClientCompany({ name, is_active: true }));
    setCreatingCompany(false);
    const id = result?.payload?.id;
    if (id) {
      formik.setFieldValue("client_company_id", id);
      formik.setFieldValue("company_name", name);
    }
  };

  useEffect(() => {
      dispatch(fetchAllSkills());
      dispatch(getClientCompanies(200, 1, { is_active: true }));
    }, [dispatch]);

    const formik = useFormik({
    initialValues: {
      title: "",
      company_name: "",
      client_company_id: "",
      job_type: "",
      skills: [],
      country: "IN",
      city: "",
      experience_required: "",
      salary: "",
      description: "",
      qualifications: "",
      additional_input: "",
    },
    validationSchema: Yup.object().shape({
      title: Yup.string().required("Job title is required"),
      company_name: Yup.string().required("Company is required"),
      client_company_id: Yup.string().nullable(),
      job_type: Yup.string().required("Job type is required"),
      country: Yup.string().required("Country is required"),
      city: Yup.string().required("City is required"),
      experience_required: Yup.string().required("Experience is required"),
      qualifications: Yup.string(),
      skills: Yup.array().min(1, "At least one skill is required"),
      description: Yup.string().required("Description is required"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      const selectedCountry = countriesList.find(
        (c) => c.iso2 === values.country
      );
      const selectedCity = citiesList.find(
        (city) =>
          city.name === values.city && city.country_id === selectedCountry?.id
      );

      const skillIds = values.skills
        .map((name) => allSkills.find((s) => s.name === name)?.id)
        .filter(Boolean);

      const payload = {
        experience_required: values.experience_required,
        salary: values.salary,
        qualifications: values.qualifications,
        job_type_id: jobTypeIdMap[values.job_type],
        job_type: values.job_type,
        country_id: selectedCountry?.id || null,
        city_id: selectedCity?.id || null,
        country: selectedCountry?.name || values.country,
        city: selectedCity?.name || values.city,
        description: values.description,
        skills: values.skills,
        skill_ids: skillIds,
        is_active: true,
        job_status_id:
          submitType === "draft"
            ? "855705f5-9696-46fc-b402-4a18ffd819af"
            : "14107328-840b-4ad0-904f-8a2243720f70",
        company_name: values.company_name,
        client_company_id: values.client_company_id || null,
        title: values.title,
        additional_input: { voice_call_description: values.additional_input },
      };

      const result = await dispatch(updateJob(jobId, payload));
      if (result?.success) {
        setSnackbar({
          open: true,
          message: "Job updated successfully!",
          severity: "success",
        });
        setTimeout(() => {
          navigate("/jobs", {
            state: {
              defaultTab: submitType === "draft" ? "Draft" : "Published",
            },
          });
        }, 800);
      } else {
        setSnackbar({
          open: true,
          message: "Failed to update job",
          severity: "error",
        });
      }

      setSubmitting(false);
    },
  });

  useEffect(() => {
    const fetchJob = async () => {
      if (jobId) {
        setInitialLoading(true);
        const job = await dispatch(fetchJobById(jobId));
        setLocalJob(job);
        setInitialLoading(false);
        if (location.state?.autoGenerate) {
          setTriggerAI(true);
        }
      }
    };
    fetchJob();
  }, [jobId, dispatch]);

  useEffect(() => {
    if (fromCreateJob) {
      const timer = setTimeout(() => setArtificialLoading(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [fromCreateJob]);

  useEffect(() => {
    setSkillOptions(allSkills.map((s) => ({ value: s.name, label: s.name })));
  }, [allSkills]);

  useEffect(() => {
    if (localJob?.id) {
      const matchedByName = (clientCompanies || []).find(
        (c) =>
          String(c.name || "").trim().toLowerCase() ===
          String(localJob.company_name || "").trim().toLowerCase(),
      );
      formik.setValues({
        title: localJob.title || "",
        company_name: localJob.company_name || "",
        client_company_id: localJob.client_company_id || matchedByName?.id || "",
        job_type: localJob.job_type?.name === "Full Time" ? "Full-Time" : "Part-Time",
        skills: localJob.skills || [],
        country: countriesList.find((c) => c.name === localJob.country?.name)?.iso2 || "IN",
        city: localJob.city?.name || "",
        experience_required: localJob.experience_required || "",
        salary: localJob.salary || "",
        description: localJob.description || "",
        qualifications: localJob.qualifications || "",
        additional_input: localJob.additional_input?.voice_call_description || "",
      });
    }
  }, [localJob?.id, clientCompanies]);

  useEffect(() => {
    const valuesAreSet =
      formik.values.title &&
      formik.values.company_name &&
      formik.values.job_type &&
      formik.values.skills.length > 0 &&
      formik.values.description;

    if (triggerAI && valuesAreSet) {
      handleGenerateWithAI();
      setTriggerAI(false);
    }
  }, [formik.values, triggerAI]);

  const jobTypeIdMap = {
    "Part-Time": "64b3c119-597e-4f8a-a66a-b448fc2baf44",
    "Full-Time": "9c4ae6ff-a326-4ddb-936e-042cdd715bc2",
  };

  useEffect(() => {
    if (localJob?.id) {
      formik.setValues({
        title: localJob.title || "",
        company_name: localJob.company_name || "",
        job_type:
          localJob.job_type?.name === "Full Time" ? "Full-Time" : "Part-Time",
        skills: localJob.skills || [],
        country:
          countriesList.find((c) => c.name === localJob.country?.name)?.iso2 ||
          "",
        city: localJob.city?.name || "",
        experience_required: localJob.experience_required || "",
        salary: localJob.salary || "",
        description: localJob.description || "",
        qualifications: localJob.qualifications || "",
        additional_input:
          localJob.additional_input?.voice_call_description || "",
      });
    }
  }, [localJob?.id]);

  const isBusy = formik.isSubmitting || busy || aiLoading;

  const handleSaveOrPublish = (type) => {
    setSubmitType(type);
    setBusy(true);

    formik.validateForm().then((errors) => {
      if (Object.keys(errors).length > 0) {
        formik.setTouched(errors);
      } else {
        formik.handleSubmit();
      }
    });
  };

  const countryOptions = useMemo(() => {
    return countriesList.map((c) => ({
      label: `${c.emoji} ${c.iso3}`,
      value: c.iso2,
      id: c.id,
      iso3: c.iso3,
    }));
  }, []);

  const cityOptions = useMemo(() => {
    const selectedCountry = countriesList.find(
      (c) => c.iso2 === formik.values.country
    );
    if (!selectedCountry) return [];
    return citiesList
      .filter((city) => city.country_id === selectedCountry.id)
      .map((city) => ({
        label: city.name,
        value: city.name,
        id: city.id,
      }));
  }, [formik.values.country]);

  const handleGenerateWithAI = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      formik.setTouched(errors);
      setNotification(
        "Please fill all required fields before generating with AI."
      );
      return;
    }

    if (!jobId) {
      setNotification("Please save the job before generating with AI.");
      return;
    }

    setNotification("");
    setAiLoading(true);
    setBusy(true);

    const aiPayload = {
      experience_required: formik.values.experience_required,
      salary: formik.values.salary,
      qualifications: formik.values.qualifications,
      job_type: formik.values.job_type,
      location: job?.country?.name || "",
      responsibilities: "",
      skills: formik.values.skills,
      company_name: formik.values.company_name,
      title: formik.values.title,
      description: formik.values.description,
    };

    try {
      let aiResponse;
      const attemptsLeft = job?.generate_description_quota_left ?? 3;

      if (attemptsLeft === 0) {
        setNotification("AI generation limit reached.");
        return;
      } else if (attemptsLeft < 3) {
        aiResponse = await dispatch(
          enhanceJobDescriptionWithAI(aiPayload, jobId)
        );
      } else {
        aiResponse = await dispatch(
          generateJobDescriptionWithAI(aiPayload, jobId)
        );
      }

      if (aiResponse?.description) {
        const cleanDescription = aiResponse.description.replace(/\*\*/g, "");
        formik.setFieldValue("description", cleanDescription);
      }
    } catch (err) {
      setNotification("Something went wrong while generating description.");
    } finally {
      setAiLoading(false);
      setBusy(false);
    }
  };

  if (initialLoading || artificialLoading) {
    return (
      <div className="create-job-page">
        <div className="loading-overlay">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading job...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="create-job-page">
      <div className="create-job-head">
        <h1>
          <img
            src="/images/leftarrow-black-icon.svg"
            alt=""
            onClick={() => navigate("/jobs")}
          />
          Edit a Job
        </h1>
      </div>
      <form onSubmit={formik.handleSubmit}>
        <div className="create-job-body">
          <div className="row">
            <div className="col-md-5">
              <div className="form-section">
                <label>Job title <span style={{color:'red'}}>*</span></label>
                <input
                  type="text"
                  name="title"
                  placeholder="Enter job title"
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={formik.touched.title && formik.errors.title ? "input-error" : ""}
                  disabled={isBusy}
                />
              </div>
            </div>
            <div className="col-md-5">
              <div className="form-section">
                <label>Company <span style={{color:'red'}}>*</span></label>
                <CreatableSelect
                  classNamePrefix="rs"
                  options={clientCompanyOptions}
                  isClearable
                  isSearchable
                  isDisabled={isBusy || creatingCompany}
                  isLoading={creatingCompany}
                  placeholder="Select or type to add Company"
                  formatCreateLabel={(input) => `Add "${input}"`}
                  onCreateOption={handleCreateCompany}
                  value={
                    clientCompanyOptions.find(
                      (o) => o.value === formik.values.client_company_id,
                    ) ||
                    (formik.values.company_name
                      ? {
                          value: formik.values.client_company_id || "",
                          label: formik.values.company_name,
                        }
                      : null)
                  }
                  onChange={(option) => {
                    formik.setFieldValue("client_company_id", option?.value || "");
                    formik.setFieldValue("company_name", option?.label || "");
                  }}
                  onBlur={() => formik.setFieldTouched("company_name", true)}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: 42,
                      borderColor:
                        formik.touched.company_name && formik.errors.company_name
                          ? "#e53e3e"
                          : base.borderColor,
                    }),
                  }}
                />

              </div>
            </div>
            <div className="col-md-5">
              <div className="form-section">
                <label>Job type <span style={{color:'red'}}>*</span></label>
                <select
                  className={formik.touched.job_type && formik.errors.job_type ? "input-error form-control mt-2" : "form-control mt-2"}
                  name="job_type"
                  value={formik.values.job_type}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={isBusy}
                >
                  <option hidden>Select job type</option>
                  <option value="Full-Time">Full Time</option>
                  <option value="Part-Time">Part Time</option>
                </select>
              </div>
            </div>
            <div className="col-md-5">
              <div className="form-section">
                <label>Skills required <span style={{color:'red'}}>*</span></label>
                <CreatableSelect
                  isMulti
                  className={formik.touched.skills && formik.errors.skills ? "input-error select mt-2" : "select mt-2"}
                  classNamePrefix="react-select"
                  name="skills"
                  placeholder="Select or type to add"
                  options={skillOptions}
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                  onChange={async (selectedOptions) => {
                    const selectedNames = selectedOptions.map(
                      (opt) => opt.value
                    );
                    formik.setFieldValue("skills", selectedNames);

                    const existingNames = allSkills.map((s) => s.name);
                    const newSkills = selectedNames.filter(
                      (name) => !existingNames.includes(name)
                    );

                    for (const skillName of newSkills) {
                      const result = await dispatch(createSkill(skillName));
                      if (result?.id) {
                        // Optionally show a toast/snackbar here
                      }
                    }
                  }}
                  onBlur={() => formik.setFieldTouched("skills", true)}
                  value={formik.values.skills.map((skill) => ({
                    value: skill,
                    label: skill,
                  }))}
                  isDisabled={isBusy}
                />
              </div>
            </div>
            <div className="col-md-5">
              <div className="form-section">
                <label>Job country <span style={{color:'red'}}>*</span></label>
                <div className="location-selects d-flex">
                  <ReactFlagsSelect
                    selected={formik.values.country}
                    onSelect={(code) => {
                      formik.setFieldValue("country", code);
                      formik.setFieldValue("city", "");
                    }}
                    countries={countryOptions.map((c) => c.value)}
                    customLabels={Object.fromEntries(
                      countryOptions.map((c) => [c.value, `${c.iso3}`])
                    )}
                    placeholder="Select country"
                    className={formik.touched.country && formik.errors.country ? "input-error country-flag-select" : "country-flag-select"}
                    selectable={!isBusy}
                  />
                </div>
              </div>
            </div>
            <div className="col-md-5">
              <div className="form-section">
                <label>Job city <span style={{color:'red'}}>*</span></label>
                <div className="location-selects d-flex">
                  <Select
                    options={cityOptions}
                    isDisabled={isBusy}
                    placeholder="Select city"
                    className={formik.touched.city && formik.errors.city ? "input-error city-select" : "city-select"}
                    classNamePrefix="react-select"
                    name="city"
                    value={
                      cityOptions.find((o) => o.value === formik.values.city) ||
                      null
                    }
                    onChange={(option) =>
                      formik.setFieldValue("city", option ? option.value : "")
                    }
                    onBlur={() => formik.setFieldTouched("city", true)}
                    disabled={isBusy}
                  />
                </div>
              </div>
            </div>
            <div className="col-md-5">
              <div className="form-section">
                <label>Experience required <span style={{color:'red'}}>*</span></label>
                <input
                  type="text"
                  name="experience_required"
                  placeholder="Enter experience required"
                  value={formik.values.experience_required}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={formik.touched.experience_required && formik.errors.experience_required ? "input-error" : ""}
                  disabled={isBusy}
                />
              </div>
            </div>
            <div className="col-md-5">
              <div className="form-section">
                <label>Job salary</label>
                <input
                  name="salary"
                  type="text"
                  placeholder="Enter salary range"
                  value={formik.values.salary}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={isBusy}
                />
              </div>
            </div>
            <div className="col-md-5">
              <div className="form-section">
                <label>Job qualifications</label>
                <input
                  name="qualifications"
                  type="text"
                  placeholder="Enter job qualifications"
                  value={formik.values.qualifications}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={isBusy}
                />
              </div>
            </div>
            <div className="col-md-10">
              <div className="form-section text-area">
                <label>
                  <div className="d-flex justify-content-between">
                    <h6>Job description <span style={{color:'red'}}>*</span></h6>
                  </div>
                  <textarea
                    name="description"
                    placeholder="Write text here ..."
                    value={formik.values.description || ""}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={formik.touched.description && formik.errors.description ? "input-error" : ""}
                    disabled={isBusy}
                  />
                  <div className="ai-generate">
                    <div className="ai-generate-wrapper">
                      <button
                        type="button"
                        className="ai-generate-btn"
                        onClick={handleGenerateWithAI}
                        disabled={busy || job?.generate_description_quota_left === 0}
                      >
                        {aiLoading ? (
                          "Generating..."
                        ) : (
                          <>
                            <img src="/images/ai-icon.png" alt="AI icon" />&nbsp;
                            {job?.generate_description_quota_left >= 3
                              ? "Generate with AI (3 Attempts left)"
                              : job?.generate_description_quota_left > 0
                              ? `Regenerate with AI (${job.generate_description_quota_left} left)`
                              : "AI Limit Reached"}
                          </>
                        )}
                      </button>
                    </div>
                    {notification && (
                      <div className="text-danger">{notification}</div>
                    )}
                  </div>
                </label>
              </div>
              <div className="job-form-inputs d-flex mt-2"></div>
            </div>
            {/* <div className="col-md-10">
              <div className="form-section text-area">
                <label>
                  <div className="d-flex justify-content-between">
                    <h6>Additional Details</h6>
                  </div>
                  <textarea
                    name="additional_input"
                    style={{ borderRadius: "8px" }}
                    placeholder="Write addititional questions here ..."
                    value={formik.values.additional_input}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={isBusy}
                  ></textarea>
                </label>
              </div>
            </div> */}
          </div>
          <div className="button-group">
            <button
              onClick={() => handleSaveOrPublish('draft')}
              disabled={isBusy}
              onMouseEnter={() => {setIsHover(true)}} onMouseLeave={() => {setIsHover(false)}}
            >
              {formik.isSubmitting && submitType === "draft" ? (
                <>
                  <img src={isHover ? "/images/pencil-alt-icon-wight.svg" : "/images/pencil-alt-icon-black.svg"} alt="" width={16} height={16} />
                  &nbsp;Saving...
                </>
              ) : (
                <>
                  <img src={isHover ? "/images/pencil-alt-icon-wight.svg" : "/images/pencil-alt-icon-black.svg"} alt="" width={16} height={16} />
                  &nbsp;Save as Draft
                </>
              )}
            </button>
            <button
              className="primary"
              onClick={() => handleSaveOrPublish('publish')}
              disabled={isBusy}
            >
              {formik.isSubmitting && submitType === "publish" ? (
                <>
                  <img src="/images/cart-plus-icon-wight.svg" alt="" width={16} height={16} />
                  &nbsp;Publishing...
                </>
              ) : (
                <>
                  <img src="/images/cart-plus-icon-wight.svg" alt="" width={16} height={16} />
                  &nbsp;Publish Now
                </>
              )}
            </button>
          </div>
        </div>
      </form>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </div>
  );
};

export default EditJob;
